// =============================================
// FORMULÁRIO DE AGENDAMENTO (CRIAR / EDITAR)
// =============================================
// Arquivo: ScheduleForm.tsx
// Rotas: /agendamentos/novo  e  /agendamentos/editar/:id
// Observação: o mesmo componente cobre criação e edição — o modo é
// determinado pela presença do parâmetro :id na URL.
// =============================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

interface ScheduleFormData {
  titulo: string;
  descricao: string;
  data_inicio: string; // formato datetime-local (YYYY-MM-DDTHH:mm)
  data_fim: string;    // formato datetime-local
  veiculo_id: string;
  motorista_id: string;
  local_agendamento: string;
  prioridade: 'baixa' | 'normal' | 'alta' | 'urgente';
  status: 'agendado' | 'confirmado' | 'em_andamento' | 'concluido' | 'cancelado' | 'reagendado';
  custo_estimado: string;
  quilometragem_agendada: string;
  observacoes: string;
}

const EMPTY_FORM: ScheduleFormData = {
  titulo: '',
  descricao: '',
  data_inicio: '',
  data_fim: '',
  veiculo_id: '',
  motorista_id: '',
  local_agendamento: '',
  prioridade: 'normal',
  status: 'agendado',
  custo_estimado: '',
  quilometragem_agendada: '',
  observacoes: ''
};

// Converte uma data ISO (do backend) para o formato aceito por input datetime-local
const toDateTimeLocal = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  // Ajusta para o fuso local e corta os segundos (YYYY-MM-DDTHH:mm)
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

const ScheduleForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState<ScheduleFormData>(EMPTY_FORM);
  const [vehicles, setVehicles] = useState<Array<{ id: number; placa: string; marca?: string; modelo?: string }>>([]);
  const [drivers, setDrivers] = useState<Array<{ id: number; nome: string }>>([]);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const loadInitialData = useCallback(async () => {
    setIsLoadingData(true);
    try {
      // Dropdowns de veículos e motoristas
      const [vehiclesResponse, driversResponse] = await Promise.all([
        apiService.getVehicles({ limit: 1000 }),
        apiService.getDrivers({ limit: 1000 })
      ]);

      if (vehiclesResponse.success && vehiclesResponse.data) {
        const list = Array.isArray(vehiclesResponse.data) ? vehiclesResponse.data : [];
        setVehicles(list.map((v: any) => ({ id: v.id, placa: v.placa, marca: v.marca, modelo: v.modelo })));
      }
      if (driversResponse.success && driversResponse.data) {
        const list = Array.isArray(driversResponse.data) ? driversResponse.data : [];
        setDrivers(list.map((d: any) => ({ id: d.id, nome: d.nome })));
      }

      // Em modo edição, carrega o agendamento existente
      if (isEdit && id) {
        const response = await apiService.getSchedule(id);
        if (response.success && response.data) {
          const a: any = response.data;
          setFormData({
            titulo: a.titulo ?? '',
            descricao: a.descricao ?? '',
            data_inicio: toDateTimeLocal(a.data_inicio),
            data_fim: toDateTimeLocal(a.data_fim),
            veiculo_id: a.veiculo_id != null ? String(a.veiculo_id) : '',
            motorista_id: a.motorista_id != null ? String(a.motorista_id) : '',
            local_agendamento: a.local_agendamento ?? '',
            prioridade: a.prioridade ?? 'normal',
            status: a.status ?? 'agendado',
            custo_estimado: a.custo_estimado != null ? String(a.custo_estimado) : '',
            quilometragem_agendada: a.quilometragem_agendada != null ? String(a.quilometragem_agendada) : '',
            observacoes: a.observacoes ?? ''
          });
        } else {
          utils.showNotification('Agendamento não encontrado', { type: 'error' });
          navigate('/agendamentos');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados do agendamento:', error);
      utils.showNotification('Erro ao carregar dados do formulário', { type: 'error' });
    } finally {
      setIsLoadingData(false);
    }
  }, [id, isEdit, navigate]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.titulo.trim()) {
      newErrors.titulo = 'Título é obrigatório';
    } else if (formData.titulo.length > 150) {
      newErrors.titulo = 'Título deve ter no máximo 150 caracteres';
    }
    if (!formData.data_inicio) {
      newErrors.data_inicio = 'Data de início é obrigatória';
    }
    if (!formData.data_fim) {
      newErrors.data_fim = 'Data de fim é obrigatória';
    }
    if (formData.data_inicio && formData.data_fim && new Date(formData.data_fim) < new Date(formData.data_inicio)) {
      newErrors.data_fim = 'A data de fim não pode ser anterior à data de início';
    }
    if (formData.custo_estimado !== '' && Number(formData.custo_estimado) < 0) {
      newErrors.custo_estimado = 'Custo estimado não pode ser negativo';
    }
    if (formData.quilometragem_agendada !== '') {
      const km = Number(formData.quilometragem_agendada);
      if (km < 0) {
        newErrors.quilometragem_agendada = 'Quilometragem não pode ser negativa';
      } else if (!Number.isInteger(km)) {
        // O backend valida quilometragem_agendada como inteiro (isInt)
        newErrors.quilometragem_agendada = 'Quilometragem deve ser um número inteiro';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      // Monta o payload apenas com campos preenchidos. Datas são enviadas em
      // ISO8601 (o backend valida com isISO8601 e a coluna é TIMESTAMP).
      const payload: Record<string, any> = {
        titulo: formData.titulo.trim(),
        data_inicio: new Date(formData.data_inicio).toISOString(),
        data_fim: new Date(formData.data_fim).toISOString(),
        prioridade: formData.prioridade,
        status: formData.status
      };
      if (formData.descricao.trim()) payload.descricao = formData.descricao.trim();
      if (formData.local_agendamento.trim()) payload.local_agendamento = formData.local_agendamento.trim();
      if (formData.veiculo_id) payload.veiculo_id = Number(formData.veiculo_id);
      if (formData.motorista_id) payload.motorista_id = Number(formData.motorista_id);
      if (formData.custo_estimado !== '') payload.custo_estimado = Number(formData.custo_estimado);
      if (formData.quilometragem_agendada !== '') payload.quilometragem_agendada = Number(formData.quilometragem_agendada);
      if (formData.observacoes.trim()) payload.observacoes = formData.observacoes.trim();

      const response = isEdit && id
        ? await apiService.updateSchedule(id, payload)
        : await apiService.createSchedule(payload);

      if (response.success) {
        utils.showNotification(
          isEdit ? 'Agendamento atualizado com sucesso' : 'Agendamento criado com sucesso',
          { type: 'success' }
        );
        navigate('/agendamentos');
      } else {
        utils.showNotification((response as any).message || 'Não foi possível salvar o agendamento', { type: 'error' });
      }
    } catch (error: any) {
      console.error('Erro ao salvar agendamento:', error);
      utils.showNotification(error?.message || 'Erro ao salvar o agendamento', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <FormLayout title={isEdit ? 'Editar Agendamento' : 'Novo Agendamento'} showActions={false}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando...</p>
        </div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title={isEdit ? 'Editar Agendamento' : 'Novo Agendamento'}
      subtitle={isEdit ? 'Atualize os dados do agendamento' : 'Preencha os dados para criar um novo agendamento'}
      isLoading={isLoading}
      onCancel={() => navigate('/agendamentos')}
      onSave={handleSubmit}
      saveButtonText={isEdit ? 'Salvar Alterações' : 'Criar Agendamento'}
      entityId={id}
    >
      <div className="form-row">
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">Título *</label>
          <input
            type="text"
            name="titulo"
            className={`form-control ${errors.titulo ? 'error' : ''}`}
            value={formData.titulo}
            onChange={handleInputChange}
            maxLength={150}
            placeholder="Ex.: Manutenção preventiva do veículo ABC-1234"
          />
          {errors.titulo && <span className="validation-message">{errors.titulo}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Data/Hora de Início *</label>
          <input
            type="datetime-local"
            name="data_inicio"
            className={`form-control ${errors.data_inicio ? 'error' : ''}`}
            value={formData.data_inicio}
            onChange={handleInputChange}
          />
          {errors.data_inicio && <span className="validation-message">{errors.data_inicio}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Data/Hora de Fim *</label>
          <input
            type="datetime-local"
            name="data_fim"
            className={`form-control ${errors.data_fim ? 'error' : ''}`}
            value={formData.data_fim}
            onChange={handleInputChange}
          />
          {errors.data_fim && <span className="validation-message">{errors.data_fim}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Veículo</label>
          <select name="veiculo_id" className="form-control" value={formData.veiculo_id} onChange={handleInputChange}>
            <option value="">— Selecione —</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>
                {v.placa}{v.marca ? ` - ${v.marca} ${v.modelo ?? ''}`.trimEnd() : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Motorista</label>
          <select name="motorista_id" className="form-control" value={formData.motorista_id} onChange={handleInputChange}>
            <option value="">— Selecione —</option>
            {drivers.map(d => (
              <option key={d.id} value={d.id}>{d.nome}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Prioridade</label>
          <select name="prioridade" className="form-control" value={formData.prioridade} onChange={handleInputChange}>
            <option value="baixa">Baixa</option>
            <option value="normal">Normal</option>
            <option value="alta">Alta</option>
            <option value="urgente">Urgente</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Status</label>
          <select name="status" className="form-control" value={formData.status} onChange={handleInputChange}>
            <option value="agendado">Agendado</option>
            <option value="confirmado">Confirmado</option>
            <option value="em_andamento">Em andamento</option>
            <option value="concluido">Concluído</option>
            <option value="cancelado">Cancelado</option>
            <option value="reagendado">Reagendado</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Local</label>
          <input
            type="text"
            name="local_agendamento"
            className="form-control"
            value={formData.local_agendamento}
            onChange={handleInputChange}
            maxLength={150}
            placeholder="Ex.: Oficina Central"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Custo Estimado (R$)</label>
          <input
            type="number"
            name="custo_estimado"
            className={`form-control ${errors.custo_estimado ? 'error' : ''}`}
            value={formData.custo_estimado}
            onChange={handleInputChange}
            min={0}
            step="0.01"
          />
          {errors.custo_estimado && <span className="validation-message">{errors.custo_estimado}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Quilometragem Agendada</label>
          <input
            type="number"
            name="quilometragem_agendada"
            className={`form-control ${errors.quilometragem_agendada ? 'error' : ''}`}
            value={formData.quilometragem_agendada}
            onChange={handleInputChange}
            min={0}
            step="1"
          />
          {errors.quilometragem_agendada && <span className="validation-message">{errors.quilometragem_agendada}</span>}
        </div>

        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">Descrição</label>
          <textarea
            name="descricao"
            className="form-control"
            value={formData.descricao}
            onChange={handleInputChange}
            rows={3}
          />
        </div>

        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">Observações</label>
          <textarea
            name="observacoes"
            className="form-control"
            value={formData.observacoes}
            onChange={handleInputChange}
            rows={3}
            maxLength={1000}
          />
        </div>
      </div>
    </FormLayout>
  );
};

export default ScheduleForm;
